import React, { useState, useEffect } from 'react';
import { Send, Loader2, Crown, X } from 'lucide-react';

export default function Dreamly() {
  const [dream, setDream] = useState('');
  const [interpretation, setInterpretation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [interpretationCount, setInterpretationCount] = useState(0);
  const [userEmail, setUserEmail] = useState('');
  const [paystackLoaded, setPaystackLoaded] = useState(false);

  // Load saved data from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('dreamly_premium');
    if (saved === 'true') {
      setIsPremium(true);
    }
    
    const savedCount = localStorage.getItem('dreamly_count');
    const lastReset = localStorage.getItem('dreamly_last_reset');
    const today = new Date().toDateString();
    
    // Reset count if it's a new day
    if (lastReset !== today) {
      localStorage.setItem('dreamly_last_reset', today);
      localStorage.setItem('dreamly_count', '0');
      setInterpretationCount(0);
    } else if (savedCount) {
      setInterpretationCount(parseInt(savedCount, 10));
    }
  }, []);

  // Load external ad scripts
  useEffect(() => {
    // Banner ad script
    const bannerScript = document.createElement('script');
    bannerScript.innerHTML = `
      atOptions = {
        'key' : '62f744269c5206fef1f12c098da0e3aa',
        'format' : 'iframe',
        'height' : 60,
        'width' : 468,
        'params' : {}
      };
    `;
    document.body.appendChild(bannerScript);
    
    const bannerInvoke = document.createElement('script');
    bannerInvoke.src = 'https://www.highperformanceformat.com/62f744269c5206fef1f12c098da0e3aa/invoke.js';
    bannerInvoke.async = true;
    document.body.appendChild(bannerInvoke);

    // Leaderboard ad script
    const leaderScript = document.createElement('script');
    leaderScript.innerHTML = `
      atOptions = {
        'key' : '898a6063dd0398f2ea2b13ee400146c5',
        'format' : 'iframe',
        'height' : 90,
        'width' : 728,
        'params' : {}
      };
    `;
    document.body.appendChild(leaderScript);
    
    const leaderInvoke = document.createElement('script');
    leaderInvoke.src = 'https://www.highperformanceformat.com/898a6063dd0398f2ea2b13ee400146c5/invoke.js';
    leaderInvoke.async = true;
    document.body.appendChild(leaderInvoke);

    return () => {
      if (document.body.contains(bannerScript)) document.body.removeChild(bannerScript);
      if (document.body.contains(bannerInvoke)) document.body.removeChild(bannerInvoke);
      if (document.body.contains(leaderScript)) document.body.removeChild(leaderScript);
      if (document.body.contains(leaderInvoke)) document.body.removeChild(leaderInvoke);
    };
  }, []);

  // Load AdSense script
  useEffect(() => {
    if (!isPremium) {
      const script = document.createElement('script');
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2055076181389061';
      script.async = true;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }, [isPremium]);

  // Load Paystack script
  useEffect(() => {
    const paystackScript = document.createElement('script');
    paystackScript.src = 'https://js.paystack.co/v1/inline.js';
    paystackScript.async = true;
    paystackScript.onload = () => setPaystackLoaded(true);
    document.body.appendChild(paystackScript);

    return () => {
      if (document.body.contains(paystackScript)) {
        document.body.removeChild(paystackScript);
      }
    };
  }, []);

  const interpretDream = async () => {
    if (!dream.trim()) {
      setError('Please share your dream before requesting an interpretation.');
      return;
    }

    if (!isPremium && interpretationCount >= 2) {
      setShowPremiumModal(true);
      return;
    }

    setLoading(true);
    setError('');
    setInterpretation('');

    try {
      const response = await fetch('/api/interpret-dream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dream })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to interpret dream');
      }

      if (data.content && data.content.length > 0) {
        const interpretationText = data.content
          .filter(item => item.type === "text")
          .map(item => item.text)
          .join("\n");
        
        setInterpretation(interpretationText);
        const newCount = interpretationCount + 1;
        setInterpretationCount(newCount);
        // Save to localStorage
        localStorage.setItem('dreamly_count', newCount.toString());
      } else {
        setError('Unable to generate interpretation. Please try again.');
      }
    } catch (err) {
      setError('An error occurred whilst interpreting your dream. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToPremium = () => {
    if (!userEmail || !userEmail.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    if (!paystackLoaded || !window.PaystackPop) {
      alert('Payment system is still loading. Please wait a moment and try again.');
      return;
    }

    const paystackKey = 'pk_live_abe1b2d54529ab200415be4bcc478a041b5b68e8';
    
    try {
      const handler = window.PaystackPop.setup({
        key: paystackKey,
        email: userEmail,
        amount: 99900,
        currency: 'NGN',
        ref: 'DREAMLY_' + Math.floor((Math.random() * 1000000000) + 1),
        plan: 'PLN_zs3zf2gaxansip6',
        metadata: {
          custom_fields: [
            {
              display_name: "Premium Subscription",
              variable_name: "subscription_type",
              value: "monthly"
            }
          ]
        },
        callback: function(response) {
          console.log('Payment successful! Reference: ' + response.reference);
          setIsPremium(true);
          setShowPremiumModal(false);
          setInterpretationCount(0);
          localStorage.setItem('dreamly_premium', 'true');
          localStorage.setItem('dreamly_count', '0');
          alert('Welcome to Premium! Your payment was successful. You now have access to unlimited interpretations, ad-free experience, and one-on-one human consultation.');
        },
        onClose: function() {
          console.log('Payment window closed');
        }
      });
      
      handler.openIframe();
    } catch (error) {
      console.error('Payment error:', error);
      alert('There was an error loading the payment system. Please refresh the page and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse top-0 left-0"></div>
        <div className="absolute w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse bottom-0 right-0 animation-delay-2000"></div>
        <div className="absolute w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animation-delay-4000"></div>
      </div>

      {/* Floating stars */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              opacity: Math.random() * 0.7 + 0.3
            }}
          ></div>
        ))}
      </div>

      {/* Floating clouds */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float-cloud"
            style={{
              top: `${15 + Math.random() * 70}%`,
              left: `-150px`,
              animationDelay: `${i * 8}s`,
              animationDuration: `${40 + Math.random() * 20}s`
            }}
          >
            <svg width="120" height="60" viewBox="0 0 120 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="30" cy="40" rx="25" ry="20" fill="white" opacity="0.4"/>
              <ellipse cx="55" cy="30" rx="30" ry="25" fill="white" opacity="0.5"/>
              <ellipse cx="80" cy="35" rx="28" ry="22" fill="white" opacity="0.45"/>
              <ellipse cx="95" cy="42" rx="20" ry="18" fill="white" opacity="0.4"/>
            </svg>
          </div>
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 max-w-4xl">
        {/* Left side ad - Only for free users on larger screens */}
        {!isPremium && (
          <div className="hidden lg:block fixed left-4 top-1/2 transform -translate-y-1/2 z-20">
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-3 border border-purple-300 border-opacity-20">
              <p className="text-purple-200 text-xs text-center mb-2">Ad</p>
              <div id="container-898a6063dd0398f2ea2b13ee400146c5"></div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="flex items-center justify-center mb-4">
            <h1 className="text-6xl font-bold text-white tracking-wide">✨ Dreamly ✨</h1>
            {isPremium && (
              <div className="ml-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                <Crown className="w-4 h-4 mr-1" />
                Premium
              </div>
            )}
          </div>
          <p className="text-purple-200 text-lg">Explore the hidden meanings within your dreams</p>
          {!isPremium && (
            <p className="text-purple-300 text-sm mt-2">
              {2 - interpretationCount} free interpretation{2 - interpretationCount !== 1 ? 's' : ''} remaining
            </p>
          )}
        </div>

        {/* Main content card */}
        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-purple-300 border-opacity-20">
          {/* Dream input */}
          <div className="mb-6">
            <label className="block text-purple-100 text-lg font-semibold mb-3">
              Share your dream
            </label>
            <textarea
              value={dream}
              onChange={(e) => setDream(e.target.value)}
              placeholder="Describe your dream in as much detail as you can remember... What did you see? How did you feel? What stood out to you?"
              className="w-full h-48 px-4 py-3 bg-white bg-opacity-20 border border-purple-300 border-opacity-30 rounded-2xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-opacity-30 transition-all duration-300 resize-none"
              disabled={loading}
            />
          </div>

          {/* Interpret button */}
          <button
            onClick={interpretDream}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-4 px-6 rounded-2xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Interpreting your dream...
              </>
            ) : (
              'Interpret my dream'
            )}
          </button>

          {/* Banner ad below button - Only for free users */}
          {!isPremium && (
            <div className="mt-6 bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-3 border border-purple-300 border-opacity-20">
              <p className="text-purple-200 text-xs text-center mb-2">Advertisement</p>
              <div className="flex justify-center">
                <div id="container-62f744269c5206fef1f12c098da0e3aa"></div>
              </div>
            </div>
          )}

          {/* Upgrade to Premium Banner - Below Button */}
          {!isPremium && (
            <div className="mt-6 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center">
                  <Crown className="w-6 h-6 text-white mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold">Upgrade to Premium</p>
                    <p className="text-white text-sm opacity-90">Unlimited • Ad-free • Human consultation</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPremiumModal(true)}
                  className="bg-white text-orange-600 font-semibold px-4 py-2 rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
                >
                  ₦999/mo
                </button>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-6 p-4 bg-red-500 bg-opacity-20 border border-red-400 border-opacity-30 rounded-2xl">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {/* Interpretation result */}
          {interpretation && (
            <div className="mt-8 animate-fade-in">
              <div className="flex items-center mb-4">
                <h2 className="text-2xl font-semibold text-purple-100">💫 Your dream interpretation</h2>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-purple-200 border-opacity-20">
                <p className="text-purple-50 leading-relaxed whitespace-pre-wrap">{interpretation}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-purple-300 text-sm">
          <p>Dreams are personal and subjective. These interpretations are meant to inspire reflection.</p>
        </div>
      </div>

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-800 to-pink-800 rounded-3xl p-8 max-w-md w-full relative animate-fade-in shadow-2xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowPremiumModal(false)}
              className="absolute top-4 right-4 text-white hover:text-purple-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">You've Used Your Free Interpretations</h2>
              <p className="text-purple-200 mb-4">Come back in 24 hours for 2 more free interpretations, or upgrade to Premium for unlimited access!</p>
            </div>

            <div className="bg-white bg-opacity-10 rounded-2xl p-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="text-green-400 mr-3 text-xl flex-shrink-0">✓</div>
                  <div>
                    <p className="text-white font-semibold">Unlimited interpretations</p>
                    <p className="text-purple-200 text-sm">No daily limits or restrictions</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="text-green-400 mr-3 text-xl flex-shrink-0">✓</div>
                  <div>
                    <p className="text-white font-semibold">Ad-free experience</p>
                    <p className="text-purple-200 text-sm">Enjoy Dreamly without interruptions</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="text-green-400 mr-3 text-xl flex-shrink-0">✓</div>
                  <div>
                    <p className="text-white font-semibold">One-on-one human consultation</p>
                    <p className="text-purple-200 text-sm">Get personalized guidance from dream experts</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="text-green-400 mr-3 text-xl flex-shrink-0">✓</div>
                  <div>
                    <p className="text-white font-semibold">Save your dream history</p>
                    <p className="text-purple-200 text-sm">Track patterns and insights over time</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="text-green-400 mr-3 text-xl flex-shrink-0">✓</div>
                  <div>
                    <p className="text-white font-semibold">Priority support</p>
                    <p className="text-purple-200 text-sm">Get help when you need it</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <p className="text-white text-4xl font-bold mb-2">₦999<span className="text-xl text-purple-200">/month</span></p>
              <p className="text-purple-200 text-sm">Cancel anytime, no commitment</p>
            </div>

            <div className="mb-6">
              <label className="block text-purple-100 text-sm font-semibold mb-2">
                Email address
              </label>
              <input
                type="email"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 bg-white bg-opacity-20 border border-purple-300 border-opacity-30 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-opacity-30 transition-all duration-300"
                required
              />
              <p className="text-purple-300 text-xs mt-2">We'll send your receipt and subscription details to this email</p>
            </div>

            <button
              onClick={handleUpgradeToPremium}
              disabled={!paystackLoaded}
              className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold py-4 px-6 rounded-2xl hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paystackLoaded ? 'Upgrade now' : 'Loading payment system...'}
            </button>

            <p className="text-center text-purple-300 text-xs mt-4">
              Secure payment via Paystack
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float-cloud {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(100vw + 150px));
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-float-cloud {
          animation: float-cloud linear infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}